import { useCallback, useState } from 'react';

import { InvoiceClientUtils } from '@ravago/shared/next-data-access/client';
import { DocumentVersionType, InvoiceToDownload } from '@ravago/shared/page-data/models/elements';
import notification from '@ravago/shared/radiance/utils/notification/notification';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { saveAs } from 'file-saver';

import { CorrelationService } from '../../../../services/tracking/correlation-token.service';

export interface UseInvoiceDocumentReturn {
   isDownloading: boolean;
   handleDownload: (invoice: InvoiceToDownload) => Promise<void>;
   handleView: (invoice: InvoiceToDownload) => void;
}

const getDocumentVersionType = (invoice: InvoiceToDownload): DocumentVersionType =>
   invoice.eInvoiceId ? DocumentVersionType.COPY_OF_ORIGINAL : DocumentVersionType.ORIGINAL;

export const useInvoiceDocument = (): UseInvoiceDocumentReturn => {
   const translate = useTranslations();
   const format = useFormatter();
   const [isDownloading, setIsDownloading] = useState(false);

   const handleDownload = useCallback(
      async (invoice: InvoiceToDownload) => {
         setIsDownloading(true);
         try {
            const correlationId = CorrelationService.generateCorrelationId('dpcid');
            const documentVersionType = getDocumentVersionType(invoice);
            const blob = await InvoiceClientUtils.downloadInvoiceDocument({
               invoiceId: invoice.invoiceId,
               documentId: invoice.documentId,
               documentVersionType,
               correlationId
            });

            const formattedDate = format.dateTime(invoice.date).replaceAll('/', '');
            const fileName = `${invoice.eInvoiceId || invoice.invoiceId}_${invoice.type.toLowerCase()}_${formattedDate}`;
            saveAs(blob, fileName);
         } catch (_) {
            notification(translate('invoices.error.download-document-failed'), { duration: 5000 });
         } finally {
            setIsDownloading(false);
         }
      },
      [format, translate]
   );

   const handleView = useCallback(
      (invoice: InvoiceToDownload) => {
         const formattedDate = format.dateTime(invoice.date).replaceAll('/', '');
         const fileName = `${invoice.eInvoiceId || invoice.invoiceId}_${invoice.type.toLowerCase()}_${formattedDate}.pdf`;
         const documentVersionType = getDocumentVersionType(invoice);
         const url = `/api/customer/invoices/${invoice.invoiceId}/documents/${invoice.documentId}?filename=${encodeURIComponent(
            fileName
         )}&documentVersionType=${documentVersionType}`;
         window.open(url, '_blank', 'noopener,noreferrer');
      },
      [format]
   );

   return { isDownloading, handleDownload, handleView };
};
